/* eslint-disable import/extensions */
/* eslint-disable react/no-array-index-key */
import React from "react";
import { Fade } from "react-awesome-reveal";
import { motion } from "framer-motion";
import {
  Compass,
  Scale,
  Search,
  Users,
  Star,
  ShieldCheck,
  BookOpen,
  HeartHandshake,
} from "lucide-react";
import { FaLinkedin } from "react-icons/fa";

import founder1 from "assets/images/founder1.png";
import founder2 from "assets/images/founder2.png";
import founder3 from "assets/images/founder3.png";
import founder4 from "assets/images/founder4.png";
import founder5 from "assets/images/founder5(1).jpeg";

export default function OurTeam() {
  return (
    <Fade triggerOnce direction="up">
      <section className="container mx-auto px-4 pt-0 pb-12">

        {/* Shared Purpose (Professional, No Logo) */}
        <div className="bg-gradient-to-r from-theme-blue/5 to-theme-purple/5 rounded-3xl p-12 shadow-lg mb-16 border border-gray-100">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl font-bold text-theme-blue mb-4 pb-4 tracking-tight">
              Shared Purpose
            </h2>
            <p className="text-base text-gray-700 leading-relaxed pb-4">
              Building <span className="text-theme-blue font-semibold">stronger boards </span>
              for  <span className="text-theme-purple font-semibold">sustainable business </span>
              and <span className="text-theme-purple font-semibold">stakeholder trust</span>.
              <br />Together, we create a space where <span className="font-medium">governance </span>
              meets <span className="font-medium">innovation</span>.
            </p>
          </div>

          {/* Core Values */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {[
              { icon: <Compass className="w-8 h-8 text-theme-purple" />, title: "Integrity", points: ["Honest, fair, ethical conduct", "Trust above transactions"] },
              { icon: <Scale className="w-8 h-8 text-theme-purple" />, title: "Independence", points: ["Objective, impartial voices", "Free from undue influence"] },
              { icon: <Search className="w-8 h-8 text-theme-purple" />, title: "Transparency", points: ["Clear processes & open disclosures", "Accountability at every step"] },
              { icon: <Users className="w-8 h-8 text-theme-purple" />, title: "Diversity & Inclusion", points: ["Gender balance, sectoral expertise", "Diverse thought leadership"] },
              { icon: <Star className="w-8 h-8 text-theme-purple" />, title: "Excellence & Accountability", points: ["Continuous training & knowledge sharing", "Responsibility to all stakeholders"] },
              { icon: <Compass className="w-8 h-8 text-theme-purple" />, title: "Pre-Screened Professional Access", points: ["Multi-layer integrity ", "background verification"] },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                className="bg-white shadow-lg rounded-2xl p-6 flex flex-col items-center text-center hover:shadow-2xl hover:-translate-y-2 transition duration-300"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                {item.icon}
                <h3 className="text-base font-semibold text-theme-blue mt-3 mb-2">
                  {item.title}
                </h3>
                <ul className="!text-sm text-gray-600 space-y-1 pt-2">
                  {item.points.map((point, i) => (
                    <li key={i}>• {point}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

      {/* Section 2: What You Get From Us */}
      <motion.div
        className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 sm:p-12 relative overflow-hidden "
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Decorative elements */}
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-theme-purple/5 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-theme-blue/5 blur-3xl" />
        
        <div className="relative z-10">
          <h2 className="text-4xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-theme-purple to-theme-blue mb-12 text-center">
            What you will get from the platform
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Curated Connections",
                desc: "Access to pre-screened independent directors with verified credentials.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                ),
              },
              {
                title: "Governance Insights",
                desc: "Thought leadership, trends, and actionable strategies for boardrooms.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                ),
              },
              {
                title: "Learning & Development",
                desc: "Workshops, resources, and training modules to strengthen decision-making.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                ),
              },
              {
                title: "Trust & Transparency",
                desc: "A platform built on integrity to foster meaningful board-company relationships.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                ),
              },
              {
                title: "Innovation & Collaboration",
                desc: "Opportunities to co-create new governance solutions for sustainable growth.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                ),
              },
              {
                title: "Continuous Support",
                desc: "End-to-end guidance for both companies and directors to maximize impact.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ),
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group hover:border-theme-purple/20"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="bg-gradient-to-br from-theme-purple to-theme-blue p-3 rounded-xl inline-flex mb-4 group-hover:scale-110 transition-transform duration-300">
                  <div className="text-white">{item.icon}</div>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3 group-hover:text-theme-purple transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="!text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

        {/* Our Founders Section */}
        <motion.div
          className="text-center max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-theme-blue mb-10 pt-32 ">
            Our Founders
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 pb-32 justify-items-center">
            {[
              {
                name: "Alok Misra",
                
                image: founder3,
                bio: "Brings deep expertise in corporate governance and risk management.",
                linkedin: "https://www.linkedin.com/in/alokcmisra/",
                id: "linkedin.com/in/alokcmisra",
              },
              {
                name: "Rahul Kapani",
                
                image: founder4,
                bio: "A consummate risk and corporate governance professional with deep management expertise in private and public enterprises",
                linkedin: "https://www.linkedin.com/in/rahul-kapani-738b1a/",
                id: "linkedin.com/in/rahul-kapani-738b1a",
              },
              {
                name: "Srinivasan V Swamy",
                
                image: founder1,
                bio: "Visionary leader driving governance excellence through innovation.",
                linkedin: "https://www.linkedin.com/in/srinivasanvswamy/",
                id: "linkedin.com/in/srinivasanvswamy",
              },
              {
                name: "Sundar Sampath",
                
                image: founder2,
                bio: "Passionate about building inclusive, high-performing boards across industries.",
                linkedin: "https://www.linkedin.com/in/sundarsampath/",
                id: "linkedin.com/in/sundarsampath",
              },
              {
                name: "Preeti Sadarangani",
                
                image: founder5,
                bio: "Offers deep governance and risk expertise.  High-impact executive coach to senior leaders",
                linkedin: "https://www.linkedin.com/in/preeti-sadarangani/",
                id: "linkedin.com/in/preeti-sadarangani",
              },
            ].map((founder, index) => (
              <a
                key={founder.name}
                href={founder.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className={`block h-full 
                 ${index === 3 ? "lg:col-start-1" : ""} 
                 ${index === 4 ? "lg:col-start-2" : ""}`} // ensures clickable area includes whole card
              >
                <motion.div
                  className="bg-white shadow-lg rounded-2xl p-6 flex flex-col items-center text-center hover:shadow-2xl hover:-translate-y-2 transition duration-300 cursor-pointer h-full"
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <img
                    src={founder.image}
                    alt={founder.name}
                    className="w-32 h-32 rounded-full object-cover mb-4 border-4 border-theme-purple/20"
                  />
                  <h3 className="text-xl font-semibold text-theme-blue">
                    {founder.name}
                  </h3>
                  <p className="text-gray-600 text-center mt-3 text-sm">
                    {founder.bio}
                  </p><br />
                  <a
                    href={founder.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-theme-blue text-white text-sm font-semibold rounded-lg hover:bg-theme-purple transition-colors"
                  >
                    <FaLinkedin className="w-8 h-6" />
                    
                  </a>
                  
                </motion.div>
              </a>
            ))}
          </div>
        </motion.div>

        {/* Shared Commitments — Straight Ribbon Layout */}
        <motion.section
          className="relative w-full bg-gradient-to-r from-theme-purple/10 to-theme-purple/5 rounded-3xl shadow-xl p-10 mb-16"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Heading */}
          <h2 className="text-3xl font-bold text-theme-purple text-center mb-16">
            Shared Commitments
          </h2>

          {/* Straight Ribbon Layout */}
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-14 md:gap-10">
            {[
              { icon: <ShieldCheck />, title: "Uphold Laws & Governance", desc: "Commitment to legal and ethical standards in all actions." },
              { icon: <ShieldCheck />, title: "Confidentiality First", desc: "Protect sensitive data and respect the privacy of all stakeholders." },
              { icon: <BookOpen />, title: "Professional Boundaries", desc: "Maintain a culture of respect across all collaborations." },
              { icon: <HeartHandshake />, title: "Foster Trust", desc: "Encouraging open learning and strong professional relationships." },
              { icon: <Star />, title: "Good Governance Culture", desc: "Actively contribute to a responsible and ethical ecosystem." },
            ].map((node, index) => (
              <motion.div
                key={index}
                className="relative flex flex-col items-center text-center max-w-[240px]"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.4 }}
              >
                {/* BIGGER CIRCLE */}
                <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-xl border-2 border-theme-purple/20 mb-6">
                  {React.cloneElement(node.icon, { className: "w-8 h-8 text-theme-purple" })}
                </div>

                {/* FIXED TITLE & TEXT */}
                <h3 className="text-lg font-semibold text-theme-purple leading-snug mb-2">
                  {node.title}
                </h3>
                <p className="text-gray-700 text-sm leading-relaxed px-3 md:px-0">
                  {node.desc}
                </p>

                {/* Connector Line */}
                {index < 4 && (
                  <div className="hidden md:block absolute top-10 left-full w-20 h-1 bg-theme-purple/20" />
                )}
              </motion.div>
            ))}
          </div>
        </motion.section>
      </section>
    </Fade>
  );
}
